import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, BarChart3, TableIcon } from "lucide-react";

import { ChartView } from "@/components/bureau/ChartView";
import { EnquiriesBanner } from "@/components/bureau/EnquiriesSheet";
import { ScoreGauge } from "@/components/bureau/ScoreGauge";
import { SummaryCards } from "@/components/bureau/SummaryCards";
import { TableView } from "@/components/bureau/TableView";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { dash, formatDate } from "@/lib/cibil/format";
import { computeMetrics } from "@/lib/cibil/metrics";
import { normalizeReport, type NormalizedReport } from "@/lib/cibil/normalize";
import {
  getDirectorCibilView,
  type DirectorCibilRow,
} from "@/services/cibilApi";

interface ViewCibilReportProps {
  director: DirectorCibilRow;
  onBack: () => void;
}

/**
 * Full director bureau report — Chart View / Table View matching the
 * CIBIL Insight underwriting UI (score gauge, summary cards, charts, ledger).
 */
const ViewCibilReport: React.FC<ViewCibilReportProps> = ({
  director,
  onBack,
}) => {
  const [report, setReport] = useState<NormalizedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState("chart");

  useEffect(() => {
    if (director.id == null) {
      setError("CIBIL report id is missing for this director.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getDirectorCibilView(director.id, director.director_uuid)
      .then((raw) => {
        if (cancelled) return;
        setReport(normalizeReport(raw));
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err && typeof err === "object" && "message" in err
            ? String((err as { message: unknown }).message)
            : err instanceof Error
              ? err.message
              : "Failed to load CIBIL report";
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [director.id, director.director_uuid]);

  const metrics = useMemo(
    () => (report ? computeMetrics(report) : null),
    [report]
  );

  const title = report?.borrowerName || director.full_name;

  return (
    <div className="w-full min-w-0 pb-6">
      <header className="sticky top-0 z-10 mb-5 flex flex-wrap items-center gap-3 border-b bg-white/90 px-2 py-3 backdrop-blur">
        <Button variant="ghost" size="sm" className="gap-1" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> Directors
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-display text-lg font-semibold">
            {loading ? "Loading report…" : dash(title)}
          </h1>
          {metrics ? (
            <p className="truncate text-xs text-muted-foreground">
              Report as on {formatDate(metrics.referenceDate)}
            </p>
          ) : null}
        </div>
        {report ? (
          <Card className="shrink-0 px-3 py-1.5">
            <ScoreGauge
              score={report.creditScore ?? director.cibil_score}
              bureau="CIBIL"
              size="xs"
            />
          </Card>
        ) : null}
      </header>

      <div className="px-2">
        {loading ? (
          <Card className="p-8 text-center text-sm text-muted-foreground">
            Loading CIBIL report…
          </Card>
        ) : error ? (
          <Card className="p-6 text-sm text-red-600">{error}</Card>
        ) : !report || !metrics ? (
          <Card className="p-6 text-sm text-muted-foreground">
            No report data available.
          </Card>
        ) : (
          <Tabs value={view} onValueChange={setView} className="gap-5">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <TabsList className="h-11 rounded-lg bg-slate-100 p-1 text-slate-500">
                  <TabsTrigger
                    value="chart"
                    className="gap-1.5 rounded-md px-4 text-slate-500 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm data-[state=inactive]:hover:text-slate-700"
                  >
                    <BarChart3 className="h-4 w-4" /> Chart View
                  </TabsTrigger>
                  <TabsTrigger
                    value="table"
                    className="gap-1.5 rounded-md px-4 text-slate-500 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm data-[state=inactive]:hover:text-slate-700"
                  >
                    <TableIcon className="h-4 w-4" /> Table View
                  </TabsTrigger>
                </TabsList>
              </div>

              <EnquiriesBanner
                report={report}
                countLastYear={metrics.enquiries365}
                referenceDate={metrics.referenceDate}
              />
              <SummaryCards metrics={metrics} report={report} />
            </div>

            <TabsContent value="chart" className="mt-0">
              <ChartView report={report} />
            </TabsContent>
            <TabsContent value="table" className="mt-0">
              <TableView accounts={report.accounts} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default ViewCibilReport;
